import * as d3 from 'd3';
import * as _ from 'lodash';

import { SvgManager } from '../../SvgManager';
import { Title } from '../../pieces/Title';
import { IPlotArea } from '../../pieces/IPlotArea';
import { PlotArea } from '../../pieces/PlotArea';
import { ICategoricalChartOptions } from '../ICategoricalChartOptions';
import { RoundingHelper, ILogger, LoggerFactory } from '../../../helpers/helpers';
import { SeriesFormat } from './SeriesFormat';
import { CategoricalChartRenderer } from './CategoricalChart.renderer';
import { CategoricalChartData } from './CategoricalChart.model';
import { map } from './CategoricalChart.mapper';
import { getDataExtent } from './getExtent';
import { infoTextColor } from '../../../../common/constants';
import { setSizeClasses } from '../setSizeClasses';

interface DataFormat {
}

export class SplitCategoricalChart {
    private _logger: ILogger;
    private _width = 800;
    private _height = 600;
    private _margins = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    };
    private _plotMargins = {
        top: 10,
        bottom: 25,
        left: 60,
        right: 20
    };
    private _plotArea: d3.Selection<any>;

    private _series: SeriesFormat[];
    private _data: DataFormat[];
    private _options: ICategoricalChartOptions = {
        isCircleVisible: true,
        isRectangleVisible: false,
        isLineVisible: false,
        isLabelsVisible: true,
        dataFormat: {
            label: d => d.name,
            identifier: d => d.id,
            value: d => d.value
        },
        color: infoTextColor,
        isHorizontalLineVisible: false,
        isTitleVisible: false
    };

    private _mappedData: CategoricalChartData[];

    private _chartArea: d3.Selection<any>;
    private _pa: IPlotArea;
    private _renderer: CategoricalChartRenderer;
    private _roudingHelper: RoundingHelper;
    private _title: Title;

    constructor(svgManager: SvgManager) {
        this._width = svgManager.getSize().width;
        this._height = svgManager.getSize().height;
        this._roudingHelper = new RoundingHelper();

        this._series = [];
        this._data = [];
        this._mappedData = [];
        const container = svgManager.container();
        this.init(container);
        svgManager.addResizeFunction((width: number, height: number) => {
            this.resize(width, height);
        });
    }

    updateSeries(series: any[]) {
        this._series = series;
        this.mapAndRender();
    }

    updateData(data: any[]) {
        this._data = data;
        this.mapAndRender();
    }

    options(options: ICategoricalChartOptions): ICategoricalChartOptions {
        if (arguments.length) {
            this._options = options;
            this.updateOptions();
            this.mapAndRender();
        }
        return this._options;
    }


    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            if (this._title) {
                this._title.ratio(value);
            }
            if (this._renderer) {
                this._renderer.ratio(value);
            }
            return this;
        }
        return this._ratio;
    }

    private actualExtent(dataExtent: number[], steppedMin: number, steppedMax: number): number[] {
        let usedMin = dataExtent[0];
        let usedMax = dataExtent[1];
        if (!isNaN(steppedMax)) {
            usedMax = steppedMax;
        }
        if (!isNaN(steppedMin)) {
            usedMin = steppedMin;
        }
        let hasMin = false;
        let hasMax = false;
        if (this._options.yMin || this._options.yMin === 0) {
            usedMin = this._options.yMin;
            hasMin = true;
        }
        if (this._options.yMax || this._options.yMax === 0) {
            usedMax = this._options.yMax;
            hasMax = true;
        }
        const rounded = this._roudingHelper.round(usedMin, usedMax);
        const extent = [hasMin ? usedMin : rounded.min, hasMax ? usedMax : rounded.max];
        return extent;
    }

    private mapAndRender() {
        this._mappedData = map(this._series, this._data, this._options);
        const min = _.min(this._mappedData.map(d => d.steppedMin));
        const max = _.max(this._mappedData.map(d => d.steppedMax));
        const dataExtent = getDataExtent(this._mappedData);
        const extent = this.actualExtent(dataExtent, min, max);
        this._renderer
            .color(this._options.color)
            .options({
                'bars': this._options.isRectangleVisible,
                'circles': this._options.isCircleVisible,
                'line': this._options.isLineVisible,
                'labels': this._options.isLabelsVisible,
                'horizontalLines': this._options.isHorizontalLineVisible
            })
            .yDomain(extent)
            .spacing(this._options.barsSpacing === null || this._options.barsSpacing === undefined ? 0.5 : this._options.barsSpacing / 100)
            .render(this._mappedData);
    }

    //////////////////////////

    private init(container) {
        this._chartArea = container
            .append('g')
            .classed('chart-area', true)
            .attr('transform', `translate(${this._margins.left},${this._margins.top})`);
        this._logger = LoggerFactory.getLogger('WideTech.Charting.SplitCategoricalChart', false, 0);

        this._title = new Title(this._chartArea);
        const plotWidth = this.plotWidth();
        const plotHeight = this.plotHeight();
        const pa = new PlotArea(this._chartArea, plotWidth, plotHeight);
        this._pa = pa;
        this._plotArea = pa.group()
            .attr('transform', `translate(${this._plotMargins.left},${this._plotMargins.top +
                (this._title.isVisible() ? this._title.height() : 0)})`);
        this._renderer = new CategoricalChartRenderer(this._plotArea, this.plotWidth(), this.plotHeight(), this._pa.clipPathId());

        this._title.translate(this.chartWidth() / 2, 0);
    }

    private chartHeight(): number {
        let { top, bottom } = this._margins;
        const ratio = this.ratio() || 1;
        top *= ratio;
        bottom *= ratio;
        const chartHeight = this._height - top - bottom;
        return chartHeight;
    }

    private chartWidth(): number {
        let { left, right } = this._margins;
        const ratio = this.ratio() || 1;
        left *= ratio;
        right *= ratio;
        const chartWidth = this._width - left - right;
        return chartWidth;
    }

    private plotHeight(): number {
        let { top, bottom } = this._plotMargins;
        const ratio = this.ratio() || 1;
        top *= ratio;
        bottom *= ratio;
        const plotHeight = this.chartHeight() - top - bottom
            - (this._title.isVisible() ? this._title.height() * ratio : 0);
        return plotHeight;
    }

    private plotWidth(): number {
        let { left, right } = this._plotMargins;
        const ratio = this.ratio() || 1;
        left *= ratio;
        right *= ratio;
        const plotWidth = this.chartWidth() - left - right;
        return plotWidth;
    }

    private updateOptions() {
        this.movePlotArea();

        const extent = this._renderer.yDomain();
        if (this._options.yMin || this._options.yMin === 0) {
            extent[0] = this._options.yMin;
        }
        if (this._options.yMax || this._options.yMax === 0) {
            extent[1] = this._options.yMax;
        }
        this._renderer.yDomain(extent);
        this.resizePlotArea();
    }

    private movePlotArea() {
        let { left, top } = this._plotMargins;
        const ratio = this.ratio() || 1;
        left *= ratio;
        top *= ratio;
        this._title.isVisible(this._options.isTitleVisible);
        this._title.text(this._options.title);

        const translateY = top + (this._title.isVisible() ? this._title.height() * ratio : 0);
        this._plotArea
            .attr('transform', `translate(${left},${translateY})`);
    }

    private resize(width: number, height: number) {
        this._width = width;
        this._height = height;
        this.movePlotArea();
        this._title.translate(this.chartWidth() / 2, 0);
        const ratio = this.ratio() || 1;
        setSizeClasses(this._chartArea, width, height, ratio);
        this.resizePlotArea();
    }

    private resizePlotArea() {
        const plotWidth = this.plotWidth();
        const plotHeight = this.plotHeight();
        this._pa.resizeFunction()(plotWidth, plotHeight);
        this._renderer.width(plotWidth)
            .height(plotHeight)
            .render(this._mappedData);
    }
}